import { computed, reactive } from '@vue/composition-api';
import { LRUCache } from '@/renderer/scripts/libs/lru/lru';
import store from '@/renderer/store';

/**
 * 用于 t-table 显示
 */
type Sort = {
  sortBy?: string; // 按照某个字段进行排序， undefined 则表示不排序
  descending: boolean; // 是否是降序
  sortType?: string;
};
export { Sort };

type Page = {
  pageName: string;
  path: string | undefined;
  spaceId: any;
  orgId: any;
  userId: number;
};

class Cache {
  // key : pageName, or { path: string; spaceId: string; orgId: string;}
  // value: Sort
  public static sortMap: LRUCache<string, string> = new LRUCache(200);
}

export default function useSort(pageName: string, defaultSort?: Sort) {
  const { userId } = store.getters;
  const page: Page = {
    pageName,
    path: undefined,
    spaceId: undefined,
    orgId: undefined,
    userId,
  };
  let localCache = Cache.sortMap.get(JSON.stringify(page));
  let finalDefault: Sort = localCache
    ? JSON.parse(localCache)
    : defaultSort || {
        sortBy: undefined, // 按照某个字段进行排序， undefined 则表示不排序
        descending: true, // 是否是降序
        sortType: undefined,
      };

  // 用于给 table 显示
  const tableSort = reactive<Sort>({
    sortBy: finalDefault.sortBy,
    descending: finalDefault.descending,
    sortType: finalDefault.sortType,
  });
  // 用于接口请求
  const innerSort = computed(() => {
    return {
      orderBy: tableSort.sortBy,
      orderByType: tableSort.descending ? 'desc' : 'asc',
      sortType: tableSort.sortType,
    };
  });
  const methods = {
    changePage(newPageName: string, path?: string, spaceId?: any, orgId?: any) {
      if (page.pageName === newPageName && (path === null || path === undefined)) {
        return;
      }
      if (
        path !== null &&
        path !== undefined &&
        path === page.path &&
        spaceId === page.spaceId &&
        orgId === page.orgId
      ) {
        return;
      }
      page.pageName = newPageName;
      page.path = path;
      page.spaceId = spaceId;
      page.orgId = orgId;
      localCache = Cache.sortMap.get(JSON.stringify(page));
      finalDefault = localCache
        ? JSON.parse(localCache)
        : defaultSort || {
            sortBy: undefined, // 按照某个字段进行排序， undefined 则表示不排序
            descending: true, // 是否是降序
            sortType: undefined,
          };
      methods.sortChange(finalDefault);
    },
    switchSort(columnName: string): Sort {
      // 排序规则：升序→降序→无序→升序......
      const newSort: Sort = tableSort;
      if (newSort.sortBy && newSort.sortBy !== columnName) {
        // 如果上一个排序规则与当前的排序规则不同，则进修改 sortBy, 其他不变
        newSort.sortBy = columnName;
      } else if (newSort.sortBy && newSort.descending) {
        // 当前是降序, 转成无序
        newSort.sortBy = undefined;
      } else if (!newSort.sortBy) {
        // 当前是无序, 转成升序
        newSort.sortBy = columnName;
        newSort.descending = false;
      } else {
        // 当前是升序, 转成降序
        newSort.sortBy = columnName;
        newSort.descending = true;
      }
      return newSort;
    },
    sortChange(newSort: Sort | undefined) {
      if (!newSort) {
        // 取消的不回到默认，如果恢复到默认，当前正在排序的状态与默认的一致，则无法取消
        tableSort.sortBy = undefined;
        tableSort.descending = true;
        tableSort.sortType = undefined;
      } else {
        tableSort.sortBy = newSort.sortBy;
        tableSort.descending = newSort.descending;
        tableSort.sortType = 'union';
      }
      Cache.sortMap.put(JSON.stringify(page), JSON.stringify(tableSort));
    },
  };

  return { innerSort, tableSort, methods };
}
